import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './auth/AuthContext';
import { useAuth } from './auth/useAuth';
import ProtectedRoute from './auth/ProtectedRoute';
import { TrackingProvider, useTrack } from './analytics/TrackingContext';
import { FeatureFlagsProvider } from './features/FeatureFlagsContext';
import Login from './pages/Login';
import Tabs from './components/Tabs';
import ResetButton from './components/ResetButton';
import ChangePasswordModal from './components/ChangePasswordModal';
import './components/styles.css';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Admin = lazy(() => import('./pages/Admin'));

function AppHeader() {
  const { user, logout, canAccessDashboard, canAccessAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showChangePassword, setShowChangePassword] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className='fixed-header'>
      <img className='LogoH' src={`${process.env.PUBLIC_URL}/images/logo.png`} alt='Logo Kia' />
      {user && (
        <nav className="app-nav">
          <Link to="/" className={`app-nav-link${location.pathname === '/' ? ' active' : ''}`}>
            Calculadora
          </Link>
          {canAccessDashboard() && (
            <Link to="/dashboard" className={`app-nav-link${location.pathname === '/dashboard' ? ' active' : ''}`}>
              Dashboard
            </Link>
          )}
          {canAccessAdmin() && (
            <Link to="/admin" className={`app-nav-link${location.pathname.startsWith('/admin') ? ' active' : ''}`}>
              Admin
            </Link>
          )}
          <div className="app-nav-user">
            <span className="app-nav-username">{user.nombre}</span>
            <button className="app-nav-logout" onClick={() => setShowChangePassword(true)} title="Cambiar contraseña">
              🔑
            </button>
            <button className="app-nav-logout" onClick={handleLogout} title="Cerrar sesión">
              Salir
            </button>
          </div>
        </nav>
      )}
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}
    </header>
  );
}

function CalculatorPage() {
  return (
    <>
      <Tabs />
      <ResetButton />
      <div className="section-container-aviso">
        <h5>*Calculos Estimados: Los vehiculos eléctricos protegen sus baterias a partir del 80% en funcion de la potencia del cargador.</h5>
        <h5>**Datos no vinculantes a oferta final</h5>
      </div>
    </>
  );
}

function AppRoutes() {
  const location = useLocation();
  const { trackEvent } = useTrack();

  // Track page navigation
  useEffect(() => {
    trackEvent('page_view', location.pathname);
  }, [location.pathname, trackEvent]);

  return (
    <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }} aria-busy="true" aria-label="Cargando contenido"><div className="spinner" role="status"><span className="sr-only">Cargando...</span></div></div>}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <CalculatorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute roles={['admin', 'nacional', 'gerente', 'jv']}>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute roles={['admin']}>
              <Admin />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    document.title = "Calculadora de Costes Marcos Automoción";
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <TrackingProvider>
        <FeatureFlagsProvider>
        <Helmet>
          <title>Calculadora de Costes Marcos Automoción</title>
          <meta name="description" content="Compara el gasto entre vehiculos de combustion y vehiculos electricos" />
          <meta property="og:image" content={`${process.env.PUBLIC_URL}/images/logo.png`} />
        </Helmet>
        <AppHeader />
        <main>
          <AppRoutes />
        </main>
        </FeatureFlagsProvider>
        </TrackingProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
